/** Paid pricing with optional early-bird discount — mirrors supabase/functions/domain/events/pricing.js */

function paidType(pricing) {
  const t = pricing?.type;
  if (t === "early-bird") return "paid-at-venue";
  return t;
}

function earlyBirdMaxRsvps(pricing) {
  if (pricing?.early_bird_max_rsvps != null) return pricing.early_bird_max_rsvps;
  return pricing?.early_bird_rsvp_cap ?? null;
}

function isRecurringSchedule(recurring) {
  return Boolean(recurring && recurring !== "none");
}

export function earlyBirdLeadMs(startsAt, cutoff) {
  const startMs = new Date(startsAt).getTime();
  const cutoffMs = new Date(cutoff).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(cutoffMs) || startMs <= cutoffMs) return null;
  return startMs - cutoffMs;
}

export function formatEarlyBirdLeadDuration(ms) {
  const hours = Math.round(ms / (1000 * 60 * 60));
  if (hours < 1) {
    const mins = Math.round(ms / (1000 * 60));
    return mins === 1 ? "1 minute" : `${mins} minutes`;
  }
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? "1 day" : `${days} days`;
  }
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

export function earlyBirdRecurringFormHint({
  recurring,
  starts_at,
  early_bird_cutoff,
  early_bird_enabled,
}) {
  if (!early_bird_enabled || !isRecurringSchedule(recurring) || !starts_at || !early_bird_cutoff) {
    return null;
  }
  const leadMs = earlyBirdLeadMs(starts_at, early_bird_cutoff);
  if (leadMs == null) return null;
  return `For each occurrence, early bird ends ${formatEarlyBirdLeadDuration(leadMs)} before start time.`;
}

function effectiveEarlyBirdCutoff(pricing, { occurrenceStartsAt = null } = {}) {
  const leadMs = pricing?.early_bird_lead_ms;
  if (leadMs != null && occurrenceStartsAt) {
    const cutoff = new Date(new Date(occurrenceStartsAt).getTime() - Number(leadMs));
    return Number.isNaN(cutoff.getTime()) ? null : cutoff;
  }

  const cutoff = pricing?.early_bird_cutoff;
  if (!cutoff) return null;
  const d = new Date(cutoff);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isPaidPricing(pricing) {
  const t = paidType(pricing);
  return t === "paid-at-venue" || t === "paid-self-managed";
}

/** Event has early-bird fields configured (may or may not still be active). */
export function hasEarlyBirdConfig(pricing) {
  if (!isPaidPricing(pricing)) return false;
  const eb = Number(pricing.early_bird_amount);
  const amt = Number(pricing.amount);
  return Number.isFinite(eb) && eb > 0 && Number.isFinite(amt) && eb < amt;
}

/** @deprecated use hasEarlyBirdConfig */
export const hasEarlyBirdOffer = hasEarlyBirdConfig;

function computeEarlyBirdActive(pricing, { rsvpCount = 0, now = new Date(), occurrenceStartsAt = null } = {}) {
  if (!hasEarlyBirdConfig(pricing)) return false;

  const cutoff = effectiveEarlyBirdCutoff(pricing, { occurrenceStartsAt });
  if (cutoff && now >= cutoff) return false;

  const max = earlyBirdMaxRsvps(pricing);
  if (max != null && Number.isFinite(Number(max))) {
    if (Number(rsvpCount) >= Number(max)) return false;
  }

  return true;
}

/** Whether early-bird price applies — prefers API-computed flag when present. */
export function isEarlyBirdActive(pricing, { rsvpCount, now = new Date(), occurrenceStartsAt = null } = {}) {
  if (!pricing || pricing.type === "free") return false;
  if (typeof pricing.early_bird_active === "boolean") return pricing.early_bird_active;
  return computeEarlyBirdActive(pricing, {
    rsvpCount: rsvpCount ?? 0,
    now,
    occurrenceStartsAt,
  });
}

export function resolveEffectivePricing(
  pricing,
  { rsvpCount = 0, now = new Date(), occurrenceStartsAt = null } = {},
) {
  if (pricing?.effective_amount != null && typeof pricing.early_bird_active === "boolean") {
    return pricing;
  }

  const base = pricing || { type: "free" };
  if (!isPaidPricing(base)) {
    return { ...base, effective_amount: null, early_bird_active: false, early_bird_spots_left: null };
  }

  const active = computeEarlyBirdActive(base, { rsvpCount, now, occurrenceStartsAt });
  const effectiveAmount = active ? base.early_bird_amount : base.amount;
  const max = earlyBirdMaxRsvps(base);
  let spotsLeft = null;
  if (active && max != null && Number.isFinite(Number(max))) {
    spotsLeft = Math.max(0, Number(max) - Number(rsvpCount || 0));
  }

  const out = {
    ...base,
    effective_amount: effectiveAmount,
    early_bird_active: active,
    early_bird_spots_left: spotsLeft,
  };
  const cutoff = effectiveEarlyBirdCutoff(base, { occurrenceStartsAt });
  if (cutoff) out.early_bird_cutoff = cutoff.toISOString();
  return out;
}

/** Current ticket price (early-bird or regular). */
export function effectiveAmount(pricing, opts) {
  const resolved = resolveEffectivePricing(pricing, opts);
  if (!pricing || pricing.type === "free") return null;
  return resolved.effective_amount;
}

/** Human-readable early-bird status lines for the event detail page. */
export function earlyBirdDetailLines(pricing, { rsvpCount, now = new Date(), currencySymbol: symFn } = {}) {
  const resolved = resolveEffectivePricing(pricing, { rsvpCount, now });
  if (!hasEarlyBirdConfig(resolved)) return [];

  const sym = symFn ? symFn(resolved.currency) : (resolved.currency || "EUR");
  const lines = [];

  if (resolved.early_bird_active) {
    lines.push(`Pay ${resolved.early_bird_amount}${sym} now (regular ${resolved.amount}${sym})`);
  } else {
    lines.push(`Early bird ended — now ${resolved.amount}${sym}`);
    return lines;
  }

  if (resolved.early_bird_cutoff) {
    try {
      const d = new Date(resolved.early_bird_cutoff);
      if (!Number.isNaN(d.getTime())) {
        const label = d.toLocaleString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
        lines.push(`Until ${label}`);
      }
    } catch {
      /* ignore */
    }
  }

  if (resolved.early_bird_spots_left != null) {
    const max = earlyBirdMaxRsvps(resolved);
    lines.push(`${resolved.early_bird_spots_left} of ${max} spots left`);
  }

  return lines;
}
